import { SendBack } from '../components/sendbacks/sendback-model';
import { ChangeApi } from './change-api';
import { DiagnosisGroup } from './trucode-domain';

// Generated using typescript-generator version 2.17.558 on 2019-09-20 12?:20?:12.

export type EditAction = 'CREATE' | 'UPDATE' | 'DELETE' | 'MOVE';
export const editActions: EditAction[] = ['CREATE', 'MOVE', 'DELETE', 'UPDATE'];

export type Disagreement = {
  workflow: string;
  disagreementCode?: string;
  disagreementDescription?: string;
  disagreementBy?: Person;
  disagreementComment?: string;
  createDate?: Date | string;
  lastDate?: Date | string;
  resolvedBy?: Person;
  // transient
  agree?: boolean;
};

export type ChangeReason<T = any> = {
  fieldName?: string;
  oldValue?: T;
  newValue?: T;
  code?: string;
  changeReasonCode?: string;
  changeReasonDescription?: string;
  comments?: string;

  // rebuttal
  disagreementResolved?: boolean;
  auditAction?: EditAction;
  reverted?: boolean;
  revertedBy?: Person;
  revertedDate?: Date;
  sourceObject?: string;
  isEM?: boolean;
  disagreementReasons?: Disagreement[];
  lineId?: string;
};

export function setReverted(a: ChangeReason, revertedBy: Person, revertedDate: Date) {
  a.reverted = true;
  a.revertedBy = revertedBy;
  a.revertedDate = revertedDate;
  a.disagreementResolved = true;
  return a;
}

/*
1 - disagree (enter disagree reason(s)) or nothing (the server will figure out if there are any disagree reasons here and will auto resolve of they don't enter comments), 

2- disagree (enter disagree reason(s)) or agree (if they agree and there isn't a coder disagreement, then resolve = true.  If there is a coder disagreement, resolve = true and reverted = true), 

3- disagree (enter disagree reason(s)) or agree (resolve = true), 

4 - agree (just mark it as resolved = true) revert (mark it as resolved = true and reverted = true)
*/
export function resolutionForIteration(iteration: number, action: 'Agree' | 'Disagree'): { disagreementResolved: boolean; reverted: boolean } {
  const disagree = { disagreementResolved: false, reverted: false };

  if (iteration < 4 && action === 'Disagree') {
    return disagree;
  }

  switch (iteration) {
    case 1:
      return disagree;
    case 2:
      if (action === 'Disagree') {
        return disagree;
      } else {
        return { disagreementResolved: true, reverted: true };
      }
    case 3:
      return { disagreementResolved: true, reverted: false };
    case 4:
      if (action === 'Agree') {
        return { disagreementResolved: true, reverted: false };
      } else {
        return { disagreementResolved: true, reverted: true };
      }
    default:
      return disagree;
  }
}

type RebuttalDisagreements = Record<string, ChangeReason[]>;

export function addDisagreement(lineId: string, original: Audit, update: Audit, workflow: string, user: Person, disagreementResolved = false, reverted = false): ChangeReason {
  const disagreementBy: Partial<Person> = {};
  disagreementBy.userId = user.userId;
  disagreementBy.firstName = user.firstName;
  disagreementBy.lastName = user.lastName;

  const lastReasons = original.changeReasons || [];
  const last = lastReasons[lastReasons.length - 1];
  const reason: Partial<ChangeReason> = {};

  if (last) {
    reason.isEM = last.isEM;
    reason.fieldName = last.fieldName;
    reason.oldValue = last.oldValue;
    reason.newValue = last.newValue;
  }

  reason.changeReasonCode = update.changeReasonCode;
  reason.changeReasonDescription = update.changeReasonDescription;
  reason.disagreementReasons ||= [];
  reason.auditAction = original.action || update.action;
  reason.sourceObject = update.sourceObject;
  reason.disagreementResolved = disagreementResolved;
  reason.reverted = reverted;
  reason.lineId = lineId;

  const d: Disagreement = {
    workflow,
    disagreementBy,
    disagreementCode: update.changeReasonCode,
    disagreementDescription: update.changeReasonDescription,
    disagreementComment: update.notes,
    createDate: new Date().toISOString(),
    agree: disagreementResolved,
  };
  reason.disagreementReasons.push(d);
  return reason;
}

export function disagreementToString(iteration: number, d: Disagreement) {
  let action = '';
  switch (iteration) {
    case 1:
      action = 'Rebuttal';
      break;
    case 2:
      action = 'Response';
      break;
    case 3:
      action = '2nd Rebuttal';
      break;
    case 4:
      action = 'Escalate';
      break;
    default:
      action = '';
  }
  if (d.agree) {
    return `${action} - Resolved`;
  }

  const comments = d.disagreementComment ? ` - ${d.disagreementComment}` : '';
  return `${action} - ${d.disagreementDescription} ${comments}`;
}

export function getRebuttalDisagreemens(worksheet: Worksheet, auditGroup: string): ChangeReason[] {
  worksheet.rebuttalDisagreements ||= {};
  worksheet.rebuttalDisagreements[auditGroup] ||= [];
  return worksheet.rebuttalDisagreements[auditGroup];
}

export function setRebuttalDisagreements(worksheet: Worksheet, auditGroup: string, reasons: ChangeReason[]) {
  worksheet.rebuttalDisagreements ||= {};
  reasons.forEach((a) => {
    if (a.disagreementReasons) {
      for (let i = 0; i < a.disagreementReasons.length; i++) {
        const d = { ...a.disagreementReasons[i] };
        delete d.agree;
        a.disagreementReasons[i] = d;
      }
    }
  });

  worksheet.rebuttalDisagreements[auditGroup] = reasons;
  return reasons;
}

export function getAuditInfo(w: Worksheet, workflow: string) {
  if (!w.auditInfo) {
    return undefined;
  }
  return w.auditInfo[workflow];
}

export function getAuditGroup(w: Worksheet, workflow: string) {
  const info: AuditInfo = getAuditInfo(w, workflow);
  if (info?.auditGroup) {
    return info.auditGroup;
  } else {
    console.info('No audit group found for workflow', workflow);
  }
  return undefined;
}

let rebuttalCache: Record<string, Record<string, ChangeReason>> = {};
function cacheKey(a: ChangeReason) {
  return a.lineId + a.changeReasonCode;
}
export function initRebuttalCache(workflow: string) {
  rebuttalCache = {};
  rebuttalCache[workflow] = {};
}

export function addRebuttalDisagreement(worksheet: Worksheet, workflow: string, reason: ChangeReason): ChangeReason[] {
  const info: AuditInfo = worksheet.auditInfo[workflow];
  if (info?.auditGroup) {
    const workflowCache = rebuttalCache[workflow] || {};
    const key = cacheKey(reason);
    workflowCache[key] = reason;

    const list = setRebuttalDisagreements(worksheet, info.auditGroup, Object.values(workflowCache) || []);
    return list;
  } else {
    console.info('No audit group found for workflow', workflow);
  }
  return [];
}

export type Audit = {
  codeLineId?: string;

  //TODO maintain from history/change Api
  changeReasons?: ChangeReason[];
  rebuttalReasons?: ChangeReason[];
  // deprecate TODO use to display the latest change reason
  changeReasonCode?: string;
  changeReasonDescription?: string;

  notes?: string;
  excludeFromAudit?: boolean;

  createdBy?: Person;
  action?: EditAction;

  createDate?: string;

  code?: string;

  oldLineNumber?: number;
  newLineNumber?: number;

  audit?: Audit;
  auditMap?: AuditMap;
  active?: boolean;
  workflow?: string;
  resolved?: boolean;

  sourceObject?: string;
  disagreementReasons?: Disagreement[];
  lineId?: string;
};

export type AuditMap = { [key: string]: Audit };

type Details = {
  articleId?: string;
  contentType?: null;
  editCategory?: string;
  editId?: string;
  editLevel?: string;
  editSubCategoryName?: string;
  editSubcategoryId?: string;
  macId?: string;
  macName?: string;
  policyTitle?: string;
  sourceData?: string;
  sourceField?: string;
  diagnosisGroups?: DiagnosisGroup[];
  measureId?: string;
  tuples?: Tuple[];
  dxCodes?: string[];
  excludeFromAudit?: boolean;
};
export type AlertType = 'CCIEDIT' | 'GLOBALEDIT' | 'LOCALEDIT' | 'MIPSALERT' | 'MODIFIEREDIT' | 'TRUCODEEDIT' | 'SYSTEMERROR' | 'MIPSEDIT';

type Tuple = {
  modifier?: string;
  description?: string;
  cptCode?: string;
};

export interface Alert extends WorksheetBase {
  componentToken?: 'ALERT';
  componentId?: string;
  alertId?: string;
  alertType?: AlertType;
  details?: Details;

  cptCode?: string;
  diagnoses?: string[];

  globalRule?: boolean;
  description?: string;
  errorText?: string;
  notes?: string;
  resolved?: boolean;
  resolvedDate?: Date;
  resolvedWho?: string;
  overridden?: boolean;
  overrideUserId?: string;
  overrideDate?: Date;

  infoOnly?: boolean;
  canOverride?: boolean;
  acknowledged?: boolean;
  acknowledgeDate?: string;
  acknowledgedWho?: string;
  acknowledgedBy?: Person;

  overrideReason?: string;
}

export interface WorksheetAlertEvent {
  worksheetId?: string;
  alertList?: Alert[];
  serializedDetails?: string;
}

export interface BillingInformation {
  billingBatchId?: string;
  isBilled?: boolean;
  dateBilled?: Date;
}

export interface CoderNote extends WorksheetBase {
  noteId?: string;
  noteDate?: Date;
  noteWhoId?: string;
  noteWhoName?: string;
  noteType?: string;
  note?: string;
  acknowledged?: boolean;
  acknowledgedWhoId?: string;
  acknowledgedWhoName?: string;
  worksheetId?: string;
}

export interface CodingLog extends WorksheetBase {
  logId?: string;
  logType?: string;
  logDate?: Date;
  logWho?: string;
  logDetails?: string;
  worksheetState?: string;
  worksheetFlag?: string;
  action?: string;
}

export type Diagnosis = Tracked & {
  diagnosisId?: string;
  diagnosisCode?: string;
  dxDescription?: string;
  lineNumber?: number;
  note?: CoderNote;
  stickyNote?: string;
  audit?: Audit;
  active?: boolean;
  presentOnAdmission?: string;
};

export interface Provider extends Person {
  providerId: string;
  providerNPI?: string;
  owner?: boolean;
}

export interface DownCode extends WorksheetBase {
  downcodeId?: string;
  actualCPTCode?: string;
  targetCPTCode?: string;
  provider: Provider;
  reason?: string; // codep
  comment?: string;
  diffAmount?: string;
  documentBundleVersion?: string;
  audit?: Audit;
}

export interface NCNBReason extends WorksheetBase {
  ncnbReasonId?: string;
  reason?: string; // codep
  reasonCode?: string;
  description?: string;
  comment?: string;
  comments?: string;
  audit?: Audit;
  workflows?: string; // - comma delimited list of supported workflows for the entry.  ALL == all workflows
  workflow?: string; // TODO: normalize config with service
  flag?: string; //  - 2 digit flag that will be displayed in the UI to indicate a NCNB
  newAction?: string; // - If routing is to occur with this selection, this specifies the routing for this entry.  This must be a legal transition for the selected workflow.
}

export interface EmElements {
  hpiCount?: number;
  rosCount?: number;
  pfshCount?: number;

  optCategory?: string;
  dataCategory?: string;
  riskCategory?: string;

  examCount?: number;

  noteType: string;
  emDriver: string;
  calculatedEM?: number;
  noppCategory?: string;
  noppEM?: number;
  timeRange?: string;
  timeBasedEM?: number;
  suggestedEM?: number;
  totalCriticalCareTime?: number;
  isMedicare?: boolean;
  severities?: EmLevel[];
  interventions?: EmLevel[];
}

export function emElementsLabel(e: EmElements): string {
  if (!e) {
    return '';
  }

  const { hpiCount, rosCount, pfshCount, examCount, optCategory, dataCategory, riskCategory } = e;

  if (optCategory) {
    return `OPT : ${optCategory}, DATA : ${dataCategory} RISK : ${riskCategory}`;
  }

  return `HPI: ${hpiCount || 0} ROS: ${rosCount || 0} PFSH: ${pfshCount || 0} EXAM: ${examCount || 0} DATA: ${dataCategory || 0} RISK:  ${riskCategory || 0} : ${optCategory || 0}`;
}

export function emElements(result: EmResult): EmElements {
  const {
    hpi,
    ros,
    pfsh,
    exam,
    calculatedEM,
    noppCategory,
    noppEM,
    timeRange,
    timeBasedEM,
    suggestedEM,
    noteType,
    emDriver,
    optLevel,
    dataLevel,
    riskLevel,
    totalCriticalCareTime,
    isMedicare,
    severities,
    interventions
  } = result;

  return {
    hpiCount: hpi,
    rosCount: ros,
    pfshCount: pfsh,
    examCount: exam,
    optCategory: optLevel,
    dataCategory: dataLevel,
    riskCategory: riskLevel,
    calculatedEM,
    noppCategory,
    noppEM,
    timeRange,
    timeBasedEM,
    suggestedEM,
    noteType,
    emDriver,
    totalCriticalCareTime,
    isMedicare,
    severities,
    interventions
  };
}

export function emResult(a: EmElements): Partial<EmResult> {
  if (!a) {
    return;
  }

  return {
    hpi: a.hpiCount,
    ros: a.rosCount,
    pfsh: a.pfshCount,
    exam: a.examCount,
    noppCategory: a.noppCategory,
    noppEM: a.noppEM,
    timeRange: a.timeRange,
    timeBasedEM: a.timeBasedEM,
    suggestedEM: a.suggestedEM,
    // v2
    noteType: a.noteType,
    emDriver: a.emDriver,
    optLevel: a.optCategory,
    dataLevel: a.dataCategory,
    riskLevel: a.riskCategory,
    calculatedEM: a.calculatedEM || a.suggestedEM,
    totalCriticalCareTime: a.totalCriticalCareTime,
    isMedicare: a.isMedicare,
  };
}

export function emResultsFromProcedures(procedures: Procedure[]): Partial<EmResult>[] {
  if (!procedures?.length) {
    return [];
  }
  return procedures.filter((a) => a.emCalculator).map((a) => emResult(a.emCalculator));
}

export type CriticalCareCode = {
  code: string;
  multiplier: number;
};


export type EmLevel = {
  emLevel: number;
  description: string
  order?:number;
  selected?: boolean
}
export interface EmResult {
  hpi?: number;
  ros?: number;
  pfsh?: number;
  exam?: number;
  opt?: number;
  data?: number;
  risk?: number;

  hpiLevel: string;
  hpiMaximum: number;
  rosLevel: string;
  rosMaximum: number;
  pfshLevel: string;
  pfshMaximum: number;
  historyLevel: string;
  historyEM: number;
  historyOkay: boolean;

  examLevel?: string;
  examMaximum?: number;
  examEM?: number;
  examOkay?: boolean;

  dataMaximum: number;

  optLevel: string;
  dataLevel: string;
  riskLevel: string;

  optMaximum: number;

  riskMaximum: number;

  mdmLevel: number;
  mdmEM: number;
  mdmOkay: boolean;

  calculatedEM: number;
  suggestedEM: number;
  errorMessages: any[];
  successful: boolean;

  noppCategory: string;
  noppEM: number;
  timeRange: string;
  timeBasedEM: number;

  noteType: string;
  emDriver: string;

  totalCriticalCareTime?: number;
  isMedicare?: boolean;
  criticalCareCodes?: CriticalCareCode[];
  interventions?: EmLevel[]
  severities?: EmLevel[]
}

export interface LogChangeVector {
  changeType?: ChangeType;
  object?: string;
  field?: string;
  fromValue?: any;
  toValue?: any;
}

export interface LostRevenue extends WorksheetBase {
  lostRevenueId?: string;
  cptCode?: string;
  cptDescription?: string;
  provider?: Provider;
  reason?: string;
  comment?: string;
  amount?: string;
  documentBundleVersion?: string;
  audit?: Audit;
}

export interface MipsResult {
  measureId?: string;
  checkOkay?: boolean;
  errorString?: string;
  instructions?: string;
  name?: string;
  tuples?: MipsTuple[];
  dxCodes?: string[];
  selectedTuple?: MipsTuple;
}

export interface MipsTuple {
  cptCode?: string;
  modifier?: string;
  description?: string;
}

export interface NdcInformation {
  ndcDescription?: string;
  ndcNumber?: string;
  ndcMeasure?: string;
  ndcQuantity?: string;
}

type ProviderStatus = {
  activeProvider?: boolean;
  providerId?: string;
};
export interface Person {
  userId?: string;
  lastName?: string;
  firstName?: string;
  middleName?: string;
  gender?: string;
  birthDate?: Date;
  degree?: string;
  email?: string;
  login?: string;
  workflow?: string;
  password?: string;
  [key: string]: ProviderStatus | any;
}

type Tracked<T = any> = {
  createDate?: Date;
  createWho?: string;
  createdBy?: Person;
  lastDate?: Date;
  lastWho?: string;
  lastBy?: string;
  audit?: Audit;
  auditMap?: AuditMap;
  changes?: ChangeApi;
};

type MessageInformation = {
  messageDate?: Date;
  messageId?: string;
};

type ResolutionInformation = {
  dateResolved?: Date;
  resolvedBy?: Person;
  howResolved?: string; // DOCUMENTATION_COMPLETE || PROVIDER_RESPONSE
  resolutionCode?: string; // IF A QUICK CODE WAS USED, WHAT IS THE RESPONSE
  resolutionComment?: string; // Provider text with resolution code if provided (will also be in comms transcript)
  documentBundleVersion?: string; // IF DOCUMENTATION COMPLETE, WHAT VERSION TRIGGERED RESOLUTION
};

export type CodeDescription = { code: string; description: string }
export type Procedure = Tracked & {
  id?: string;
  procedureId?: string;
  cptCode?: string;
  provider?: Provider;
  secondaryProvider?: Provider;
  tertiaryProvider?: Provider;
  description?: string;
  lineNumber?: number;
  modifiers?: string[];
  serviceDate?: Date;
  diagnosisList?: string[];
  dateOfService?: Date;
  serviceLocation?: CodeDescription;
  multiplier?: number;
  ndcInformation?: NdcInformation;
  docBundleVersion?: number | string;
  docBundleId?: string; // verify DB
  rvuPhysician?: string;
  rvuFacility?: string;
  chargeMasterAmount?: string;
  isEMCode?: boolean;
  isObservation?: boolean;
  promptNDC?: boolean;
  globalDays?: string;
  isBilled?: boolean;
  billingInformation?: BillingInformation;
  emCalculator?: EmElements;
  aiEMElements?: EmElements;
  mipsMeasureId?: string;
  isMIPSCode?: boolean;
  stickyNote?: string;
  note?: CoderNote;
  links?: string;
  modifier?: string;
  audit?: Audit;
  active?: boolean;
  fromAutoCoder?: boolean;
  autoCoder?: string;
  autoCoderConfidence?: string;
  autoCoderNote?: string;
};

export interface CoderNote {
  note?: string;
  worksheetId?: string;
  coderNoteId?: string;
  silenced?: boolean;
  noteDate?: Date;
  acknowledgedByUser?: boolean;
  author?: Person;
  active?: boolean;
  //custom
  context?: string;
  label?: string;
}

export type WorksheetBase = Tracked & {
  visitId?: string;
  worksheetId?: string;
  deleted?: boolean;
  createDate?: Date;
  createWho?: string;
  lastDate?: Date;
  lastWho?: string;
  id?: any;
  active?: boolean;
  audit?: Audit;
  label?: string;
  messagesInformation?: MessageInformation[];
  isResolved?: boolean;
  resolutionInformation?: ResolutionInformation;
  editable?: boolean;
};

type AuditRule = {
  auditRuleId?: string;
  auditRuleName?: string;
  auditRuleCreateWho?: string;
};
type AuditInfo = {
  auditGroup?: string;
  worksheetArchiveId?: string;
  previousWorkflow?: string;
  auditWorkflow?: string;
  auditedUserId?: string;
  auditorUserId?: string;
  currentRebuttalWorkflow?: string;
  rebuttalStage?: string;
  hasAuditChanged?: boolean;
  hasDisagreement?: boolean;
  active?: boolean;
  iteration?: number;
};

type AuditRecord = Record<string, AuditInfo> & {
  auditRules?: AuditRule[];
};


export function auditRecord(workflow: string, existing?: AuditRecord): AuditRecord {
  const record = existing || {};
  record[workflow] = record[workflow] || {};
  return record;
}
export interface Worksheet extends WorksheetBase {
  visitId?: string;
  departmentCode?: string;
  worksheetType?: string;
  locationCode?: string;
  mrn?: string;
  mrnGroupId?: string;
  accountNumber?: string;
  serviceDate?: string;
  dischargeDate?: string;
  owningProvider?: Provider;
  workflowOwners?: Person[];
  coder?: Person;
  state?: string;
  standardizedState?: string;
  flags?: string[];
  firstDateInCoding?: string;
  diagnoses?: Diagnosis[];
  procedures?: Procedure[];
  lostRevenues?: LostRevenue[];
  downCodes?: DownCode[];
  trackingCodes?: TrackingCode[];
  sendBacks?: ReturnItem[];
  pastWorksheets?: Worksheet[];
  complete?: boolean;
  reasonForVisit?: Diagnosis[];

  procedureMoves?: Audit[];
  diagnosisMoves?: Audit[];
  reasonForVisitMoves?: Audit[];

  procedureMove?: Audit;
  proceduresMoveUndoList?: any;
  diagnosisMove?: Audit;
  diagnosesMoveUndoList?: any;
  auditInfo?: AuditRecord;
  hasAuditChanged?: boolean;
  rebuttalDisagreements?: RebuttalDisagreements;
  rebuttalIteration?: number;
}

export interface WorksheetLog extends WorksheetBase {
  targetWsComponentId?: string;
  targetWsComponentToken?: string;
  changeType?: ChangeType;
  note?: string;
}

export interface WorksheetPK {
  worksheetId?: string;
  wsComponentToken?: string;
  wsComponentId?: string;
}

export interface WorksheetUpdate {
  visitId?: string;
  worksheetId?: string;
  departmentCode?: string;
  worksheetType?: string;
  state?: string;
  flag?: string;
  coder?: Person;
  attending?: Person;
  lastDate?: Date;
}

export interface StackTraceElement {
  classLoaderName?: string;
  moduleName?: string;
  moduleVersion?: string;
  methodName?: string;
  fileName?: string;
  lineNumber?: number;
  className?: string;
  nativeMethod?: boolean;
}

type ChangeType = 'TRANSITION' | 'CREATE' | 'UPDATE' | 'DELETE' | 'DOWNLOADDOCUMENT';

export interface Insurance {
  setId?: number;
  companyId?: string;
  companyName?: string;
  insuranceName?: string;
  isMedicare?: boolean;
  isMedicaid?: boolean;
}

type CustomFields = {
  [key: string]: string;
};
export interface Visit {
  visitId?: string;
  locationCode: string;
  queueState?: string;
  serviceDate?: string;
  registrationDate?: string;
  dischargeDate?: string;
  accountNumber?: string;
  departmentCode?: string;
  mrn?: string;
  mrnGroupId?: string;
  patient?: Person;
  chiefComplaint?: string;
  complaints?: string[];
  insurances?: Insurance[];
  createDate?: Date;
  createWho?: string;
  lastDate?: Date;
  lastWho?: string;
  firstDateInCoding?: string;
  wsSummaries?: WorksheetSummary[];
  timeInQueue?: number;
  bundleUrl?: string;
  customFields?: CustomFields;
  customFieldsArray?: object[];
  owner?: Provider;
  readonly?: boolean;
  summaryMap?: SummaryMap;
  worksheetId?: string;
  workflow?: string;
}

export type WorksheetState =
  | 'UNASSIGNED'
  | 'CHECKEDOUT'
  | 'STASHED'
  | 'RETURNED_UPDATED'
  | 'RETURNEDTOCODER'
  | 'DOWNCODED'
  | 'TRANSFERRED'
  | 'CODING'
  | 'CODED'
  | 'QA'
  | 'FINALIZED'
  | 'COMMENT'
  | 'DOCUMENTATION'
  | 'BILLING'
  | 'NOCODE'
  | 'EMPTY'
  | 'HIDDEN'
  | 'CODINGREVIEW';

// TODO: should be replaced by config by workforflow
export const knownCodingstates = [
  { label: 'Unassigned', name: 'UNASSIGNED' },
  { label: 'Finalized', name: 'FINALIZED' },
  { label: 'Coded', name: 'CODED' },
  { label: 'Stashed', name: 'STASHED' },
  { label: 'Audit', name: 'AUDIT' },
  { label: 'Coding Review', name: 'CODINGREVIEW' },
  { label: 'Coded', name: 'CODED' },
  { label: 'Coding', name: 'CODING' },
  { label: 'Returned to Coder', name: 'RETURNEDTOCODER' },
  { label: 'Review Stashed', name: 'CRSTASHED' },
  { label: 'Checked Out', name: 'CHECKEDOUT' },
  { label: 'Checked Out For Review', name: 'CHECKEDOUTFORREVIEW' },
  { label: 'Downcoded', name: 'DOWNCODED' },
  { label: 'Documentation', name: 'DOCUMENTATION' },
];

/*

QA Block semantics :

CR - Coder Routed - coder forced to QA Code Review

RP - Return to provider

RI - Revenue Impact (could be down code, lost revenue, etc)

OA - Overridden Alerts

CA - Codes assigned (Obs, CC, etc)

FQ - Forced QA - based on custom rule 
*/

export type CoderReviewQuestion = {
  worksheetId?: string;
  reasonCode?: string;
  description?: string;
  question?: string;

  coderReviewQuestionId?: string;

  reply?: string;
  active?: boolean;
  questionBy?: Person;
  replyBy?: Person;
  createDate?: string;
  createWho?: string;
  createdBy?: Person;

  reason?: ReturnItem;
  reviewQuestion?: CoderReviewQuestion;
};

export type WorksheetStateAction = {
  action: string;
  label: string;
  state: string;
  activity: string;
};

export type WorksheetStateLabel =
  | 'Unassigned'
  | 'Checked Out'
  | 'Stashed'
  | 'Coding Review'
  | 'Coded'
  | 'QA'
  | 'Returned to Coder'
  | 'Returned to Provider'
  | 'No Code'
  | 'Force to Code Review'
  | 'Returned to Coder'
  | 'Checked out for Review'
  | 'Audited'
  | 'Audit'
  | 'Skipped Audit'
  | 'Finalized'
  | 'Returned to Code Reviewer'
  | 'Sent to Provider'

export type StateInfo = {
  name: string;
  type: string;
  label: WorksheetStateLabel;
  isStart: boolean;
  workflowActivity: string;
  actions: WorksheetStateAction[];
  start: true;
  standardizedState?: string;
};

export type WorksheetStateInfo = {
  stateInfo: StateInfo;
};

export type Flag = {
  code: string; // two letter flag code to be displayed in block
  description: string; // hover text for the flag
  resolved?: boolean; // false or true
  employee?: Person; // person who resolved the flag
  field1?: string; // custom field
  field2?: string; // custom field
  comments?: string; // flag comments 
};

type MapStatus = {
  Active?: number;
  Inactive?: number;
  Billed?: number;
};
type SummaryMap = Record<string, MapStatus>;

export type WorksheetSummary = WorksheetStateInfo & {
  worksheetId?: string;
  departmentCode?: string;
  worksheetType?: string;
  coder?: Person;
  workflowOwners?: Provider[];
  owningProvider?: Provider;
  state?: WorksheetState;
  flags?: Flag[];
  createDate?: Date;
  enqDate?: Date;
  serviceDate?: Date | string;
  editable?: boolean;
  prevWorkflow?: string;
  prevWorkflowOwner?: Person;
};

export const CPT = 'PROC_CPT';
export const HCPCS = 'PROC_HCPCS';
export const ICD_10 = 'ICD_10';

export interface Description {
  description: string;
  longDescription?: string;
  shortDescription?: string;
}

export interface Flags {
  promptNDC: boolean;
  tracking: boolean;
  obs: boolean;
  custom: boolean;
  em: boolean;
  hcpcs: boolean;
}

export interface RvuInfo {
  hcpcsCode: string;
  modifier: string;
  status: string;
  workRVU: string;
  nonFacPracticeExpense: string;
  facPracticeExpense: string;
  malpracticeRVU: string;
  totalNonFacRVU: string;
  totalFacRVU: string;
  globalDays: string;
}

export type CodeType = 'PROC_CPT' | 'PROC_HCPCS' | 'ICD_10' | 'MOD_CPT' | 'MOD_HCPCS';
export interface Description {
  longDescription?: string;
  shortDescription?: string;
}
export interface CodeSet extends Omit<Diagnosis, 'description'>, Omit<Procedure, 'description'> {
  flags?: Flags;
  code?: string;
  codeType?: CodeType;
  year?: number;
  locationCode?: string;
  description?: Description;
  shortDescription?: string;
  dxDescription?: string;
  dxWithDecimal?: string;
  longDescription?: string;
  rvuInfoList?: any[];
  promptNDC?: boolean;
  tracking?: boolean;
  em?: boolean;
  hcpcs?: boolean;
  obs?: boolean;
  custom?: boolean;

  // meta data
  lineNumber?: number;
  provider?: Provider;
  serviceDate?: Date;
  cptCode?: string;
  links?: string;
  diagnosisList?: string[];
  diagnosisCode?: string;
  modifier?: string;
  active?: boolean;
}

export type TransitionAction = 'CHECKOUT' | 'STASH' | 'UNASSIGN' | 'FINISH_CODING' | 'FINALIZE' | 'RETURN' | 'ASSIGN';

export const worksheetActions: TransitionAction[] = [];

export interface TransitionRequest {
  worksheetId: string;
  action?: TransitionAction;
  force?: boolean;
  transitionNote?: string;
}

export interface TrackingCode extends WorksheetBase {
  trackingCodeId?: string;
  code?: string;
  description?: string;
  notes?: string;
  quantity?: number;
  startDateTime?: Date;
  endDateTime?: Date;
  provider?: Provider;
}

export interface LocationDepartmentAccount {
  location?: string;
  department?: string;
  account?: string;
  attendingId?: string;
  serviceDate?: string;
}

export interface WorksheetId {
  worksheetId: string;
}

export type WorksheetComponents = WorksheetStateInfo & {
  worksheetId: string;
  worksheet: Worksheet;
  alerts: Alert[];
  coderNotes: CoderNote[];
  downCodes: DownCode[];
  ncnbReasons: NCNBReason[];
  lostRevenues: LostRevenue[];
  worksheetTrackingCodes: TrackingCode[];
  sendbacks: ReturnItem[];
  coderReviewQuestions: CoderReviewQuestion[];
};

export type SendBackCode = Record<string, string | boolean>;
export type CodeReturnText = {
  workflow: string;
  code: string;
  description: string;
  returnText: string;
  flag: string;
  comment: string;
};

export type ReturnItem = WorksheetBase & {
  code: string;
  returnText: string; // this should include the “canned” return text plus any additional text added by the coder.

  description?: string;
  sendToList?: Person[];
  notes?: string;
  sendbackId?: string;

  documentBundleVersion?: string;

  selected?: boolean;
  comment?: string;
  reason?: ReturnItem;
  audit?: Audit;

  workflows?: string;
  category?: string;
  subCategory?: string;
  flag?: string;
  newAction?: string;
  tags?: string;

  isResolved?: boolean;
  resolutionInformation?: ResolutionInformation;
  messageInformation?: MessageInformation[];
  codeReturnTextList?: CodeReturnText[];

  sendbackCodes?: SendBack[];
  cancelCode?: string | number;
};

type LabelCount = {
  label: string;
  count: number;
};

export type ProductivityLocation = {
  location: string;
  states: LabelCount[];
};

type DepartmentMap = {
  [key: string]: any;
};

export type ProductivityEntry = {
  department: string;
  locations?: ProductivityLocation[];
};

export type ProductivityMap = {
  departmentMap: { [key: string]: DepartmentMap };
};

export function productivityList(response: ProductivityMap): ProductivityEntry[] {
  if (!response?.departmentMap) {
    return [];
  }
  return Object.entries(response.departmentMap).map(([department, obj]) => {
    const entry: ProductivityEntry = { department };
    entry.locations = Object.entries(obj).map(([location, states]) => ({
      location,
      states,
    }));
    return entry;
  });
}

export function visitWorkflowOwner(a: Visit): Provider | null {
  if (!a?.wsSummaries?.length) {
    return null;
  }
  const list = a.wsSummaries[0].workflowOwners;
  if (!list?.length) {
    return null;
  }
  if (list.length === 1) {
    return list[0];
  }
  return list.filter((v) => v.owner)[0] || list[0];
}

export function summaryWithActivity(summaries: WorksheetSummary[], activity: string): WorksheetSummary {
  if (!summaries?.length) {
    return null;
  }
  return summaries.find((v) => v?.stateInfo?.workflowActivity === activity);
}

export function summaryOwner(a: Visit, activity?: string): Person | null {
  if (!a?.wsSummaries?.length) {
    return null;
  }

  if (activity) {
    const summary = summaryWithActivity(a.wsSummaries, activity);
    if (summary?.prevWorkflowOwner) {
      return summary.prevWorkflowOwner;
    }
  }

  let list = a.wsSummaries[a.wsSummaries.length - 1].workflowOwners;
  if (!list?.length) {
    return null;
  }
  list = list.filter((v) => v.firstName !== 'System');

  if (list.length === 1) {
    return list[0];
  }
  return list[list.length - 1];
}

export function worksheetToVisit(a: Worksheet) {
  const b: Visit = { ...(a as Visit) };
  const summary: WorksheetSummary = {
    ...a,
    ...{
      stateInfo: {
        name: a.state,
      },
    },
  } as any;

  b.wsSummaries = [summary];

  return b;
}

export function summaryMapString(a: Visit, b: WorksheetSummary): string {
  const empty = '0/0/0';
  if (!a?.summaryMap || !b?.worksheetType) {
    return empty;
  }
  const s: MapStatus = a.summaryMap[b.worksheetType];
  if (!s) {
    return empty;
  }
  const { Active, Inactive, Billed } = s;
  return `${Active || 0}/${Inactive || 0}/${Billed || 0}`;
}

export function summariesByState(a: WorksheetSummary[]): Record<string, WorksheetSummary[]> {
  const map = {};
  if (a?.length) {
    a.forEach((v) => {
      map[v.state] = map[v.state] || [];
      map[v.state].push(v);
    });
  }
  return map;
}
export function serviceDate(b: Worksheet | WorksheetSummary): string {
  return ((b?.serviceDate as string) || '').substring(0, 10);
}
export function serviceState(b: Worksheet | WorksheetSummary): string {
  return serviceDate(b) + ' ' + b.state;
}
export function summariesString(a: WorksheetSummary[]): string {
  if (!a.length) {
    return '';
  }
  let out = '\n';

  a.forEach((b) => {
    out += b.worksheetType;
    out += '\n';
    out += serviceState(b);
    out += '\n';

    if (b.coder) {
      out += b.coder.userId;
      out += '\n';
    }
    out += '\n';
  });
  return out;
}

export function completeState(s: string) {
  return s && !(s.indexOf('CHECKEDOUT') > -1);
}

export function matchAction(state: string, actions: WorksheetStateAction[]): WorksheetStateAction | null {
  if (!actions || !state) {
    return null;
  }
  return actions.find((a) => a.state === state);
}

export function getPrimaryInsuranceLabel(a: Insurance[]) {
  if (!a?.length) {
    return '';
  }
  const p = a.find((v) => v.setId === 1);
  if (p) {
    return p.companyName || '';
  }

  return a[0].companyName || '';
}


export function validatePlaceOfService(cpt:Procedure[], defaultLocation?: CodeDescription) {
  if (!cpt || !cpt.length) {
    return;
  }

  let found:CodeDescription = defaultLocation;
  cpt.forEach((a) => {
    if (a.serviceLocation) {
      found = a.serviceLocation;
    } else {
      a.serviceLocation = defaultLocation;
    }
  });
}